import { useCallback, useEffect, useRef, useState } from "react";
import type { FlowKind, KratosFlow, SubmitOutcome } from "@/lib/kratos";
import {
  createFlow,
  getFlow,
  KratosFlowExpiredError,
  kratosErrorCopy,
  submitFlow,
} from "@/lib/kratos";

export type UseKratosFlowOptions = {
  /** Adopt an already-fetched flow (e.g. registration `continue_with`)
   * instead of creating one. */
  initialFlow?: KratosFlow;
  /** Resume an existing flow (deep link `?flow=`) instead of creating
   * a fresh one. Falls back to a fresh flow when expired/foreign. */
  flowId?: string;
  /** Extra query params for flow creation (`refresh`, `return_to`, ...). */
  createParams?: Record<string, string>;
  /** Called once per terminal success (login/registration completed). */
  onSuccess?: (outcome: Extract<SubmitOutcome, { kind: "success" }>) => void | Promise<void>;
  /** Called when a submit hands back a *different* flow kind (e.g.
   * registration → verification via continue_with/422). */
  onFlowChange?: (flow: KratosFlow, flowKind: FlowKind) => void;
};

/** Lifecycle for one Kratos self-service flow: create (or resume) on
 * mount, submit with transparent re-create on expiry, keep the latest
 * flow (with its validation messages) in state. */
export function useKratosFlow(kind: FlowKind, options: UseKratosFlowOptions = {}) {
  const [flow, setFlow] = useState<KratosFlow | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Options live in a ref so flow creation doesn't re-run when callers
  // pass inline callbacks.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const initialize = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { initialFlow, flowId, createParams } = optionsRef.current;
      if (initialFlow) {
        setFlow(initialFlow);
        return;
      }
      if (flowId) {
        try {
          setFlow(await getFlow(kind, flowId));
          return;
        } catch (err) {
          if (!(err instanceof KratosFlowExpiredError)) throw err;
          // Expired deep link — fall through to a fresh flow.
        }
      }
      setFlow(await createFlow(kind, createParams));
    } catch (err) {
      setError(kratosErrorCopy(err));
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  const submit = useCallback(
    async (values: Record<string, unknown>): Promise<SubmitOutcome | null> => {
      if (!flow) return null;
      setSubmitting(true);
      setError(null);
      try {
        let outcome: SubmitOutcome;
        try {
          outcome = await submitFlow(kind, flow, values);
        } catch (err) {
          if (!(err instanceof KratosFlowExpiredError)) throw err;
          // Transparent expiry recovery: fresh flow (fresh CSRF), one retry.
          const fresh = await createFlow(kind, optionsRef.current.createParams);
          setFlow(fresh);
          outcome = await submitFlow(kind, fresh, values);
        }
        if (outcome.kind === "success") {
          await optionsRef.current.onSuccess?.(outcome);
          return outcome;
        }
        setFlow(outcome.flow);
        if (outcome.flowKind !== kind) {
          optionsRef.current.onFlowChange?.(outcome.flow, outcome.flowKind);
        }
        return outcome;
      } catch (err) {
        setError(kratosErrorCopy(err));
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [flow, kind],
  );

  return {
    flow,
    loading,
    submitting,
    /** Hard client/transport error copy — per-field validation lives on
     * the flow itself (`nodeMessages` / `firstFlowError`). */
    error,
    setError,
    submit,
    restart: initialize,
  };
}
